import { useState, useEffect, useRef } from "react";
import { ColumnDef, flexRender, getCoreRowModel, getSortedRowModel, SortingState, useReactTable } from "@tanstack/react-table";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import Pagination from "@/common/Table/ItemsPerPage";
import { Link } from "react-router-dom";
import { getNestedValue } from "@/utils/helper/form";
import moment from "moment";
import { RoundLoader } from "@/common/ui/Loader";

interface SimpleTableViewProps<T> {
  className?: string;
  data: T[];
  columnsConfig: ColumnDef<T>[];
  columnClassName?: string;
  tableClassName?: string;
  paginationConfig?: {
    initialPage?: number;
    initialPerPage?: number;
    perPageOptions?: number[];
  };
  linkColumns?: { id: string; url: string; identifier: string; isExternal?: boolean }[];
  hidePagination?: boolean;
  isLoading?: boolean;
  highlightId?: string;
  // External pagination props
  externalPagination?: {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    perPage: { value: number; label: string };
    setPerPage?: (count: { value: number; label: string }) => void;
  };
}

const SimpleTableView = <T extends { id: string }>({
  className = "",
  data,
  columnsConfig,
  columnClassName = "max-w-[200px] w-[200px] min-w-[200px]",
  paginationConfig = { initialPage: 1, initialPerPage: 25, perPageOptions: [10, 25, 50, 100] },
  linkColumns,
  hidePagination = false,
  isLoading = false,
  tableClassName = "w-full",
  externalPagination,
  highlightId, // NEW: Destructure highlightId
}: SimpleTableViewProps<T>) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [currentPage, setCurrentPage] = useState(paginationConfig.initialPage!);
  const [perPage, setPerPage] = useState({ value: paginationConfig.initialPerPage!, label: `Records per page ${paginationConfig.initialPerPage!}` });
  const tableRef = useRef<HTMLDivElement>(null); // NEW: Ref for table container

  const table = useReactTable({
    data: data,
    columns: columnsConfig,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
  });

  useEffect(() => {
    if (highlightId && tableRef.current) {
      const targetRow = tableRef.current.querySelector(`[data-row-id="${highlightId}"]`) as HTMLElement;
      if (targetRow) {
        targetRow.classList.add("bg-brand-gray", "animate-pulse");
        // Scroll into view smoothly
        targetRow.scrollIntoView({ behavior: "smooth", block: "center" });

        // Remove pulse animation after 0.5s
        const pulseTimer = setTimeout(() => {
          targetRow.classList.remove("animate-pulse");
        }, 500);
        // remove after 30s
        const highlightTimer = setTimeout(() => {
          targetRow.classList.remove("bg-brand-gray");
        }, 30000);

        // Cleanup timers
        return () => {
          clearTimeout(pulseTimer);
          clearTimeout(highlightTimer);
        };
      }
    }
  }, [highlightId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <RoundLoader />
      </div>
    );
  }

  return data.length > 0 ? (
    <div className={className}>
      <div className="relative h-full" ref={tableRef}>
        <div className={`rounded-md border border-stroke-light h-full ${tableClassName}`}>
          <Table className={`border-separate border-spacing-0 `}>
            <TableHeader className="sticky top-0 z-10 bg-white whitespace-nowrap">
              <TableRow className="w-fit">
                {table.getHeaderGroups().map((headerGroup) =>
                  headerGroup.headers.map((header) => (
                    <TableHead key={header.id} className={`!text-text-default font-bold ${columnClassName} border-b border-r border-stroke-light`}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row: any) => {
                  return (
                    <TableRow
                      key={row.id}
                      data-row-id={row.original.id}
                      className="group relative cursor-pointer hover:bg-gray-50 transition-colors duration-150"
                    >
                      {row.getVisibleCells().map((cell: any) => {
                        if (cell.column.id === "select") return null;
                        const cellContent = flexRender(cell.column.columnDef.cell, cell.getContext());
                        const linkConfig = linkColumns?.find((link) => link.id === cell.column.id);
                        const identifierValue = linkConfig ? getNestedValue(row.original, linkConfig.identifier) : null;

                        return (
                          <TableCell
                            key={cell.id}
                            className="!text-text-default p-[5px] font-normal !leading-normal !text-13 border-b border-r border-stroke-light"
                          >
                            {linkConfig && identifierValue ? (
                              linkConfig.isExternal ? (
                                <a
                                  href={`${linkConfig.url.replace(":identifier", identifierValue)}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary hover:underline text-text-link"
                                >
                                  {cellContent}
                                </a>
                              ) : (
                                <Link to={`${linkConfig.url.replace(":identifier", identifierValue)}`} className="text-primary hover:underline text-text-link">
                                  {cellContent}
                                </Link>
                              )
                            ) : cell.column.id === "added_ts" || cell.column.id === "updatedTs" ? (
                              <span>{moment(row.original[cell.column.id]).format("DD-MMM-YYYY h:mm A")}</span>
                            ) : (
                              cellContent
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={columnsConfig.length} className="h-24 text-center">
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {!hidePagination && (
        <Pagination
          currentPage={externalPagination ? externalPagination.currentPage : currentPage}
          totalPages={
            externalPagination && externalPagination.totalPages !== undefined
              ? externalPagination.totalPages
              : Math.ceil(
                  table.getFilteredRowModel().rows.length /
                    (externalPagination && externalPagination.perPage ? externalPagination.perPage.value : perPage.value)
                )
          }
          perPage={externalPagination ? externalPagination.perPage : perPage}
          perPageOptions={paginationConfig.perPageOptions!}
          onPageChange={(page: any) => {
            if (externalPagination) {
              externalPagination.onPageChange(page);
            } else {
              setCurrentPage(page);
            }
          }}
          onPerPageChange={(count: any) => {
            if (externalPagination && externalPagination.setPerPage) {
              externalPagination.setPerPage(count);
            } else {
              setPerPage(count);
            }

            if (!externalPagination) {
              setCurrentPage(1);
            }
          }}
        />
      )}
    </div>
  ) : (
    <div className={`${className} flex items-center justify-center h-64`}>
      <div className="text-sm text-gray-500">No data available</div>
    </div>
  );
};

export default SimpleTableView;
